import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const MOCK_USER_KEY = "gundamwar.auth.mockUser.v1";
const HAS_GOOGLE_CLIENT_ID = Boolean(GOOGLE_CLIENT_ID);
const HAS_API_BASE_URL = Boolean(API_BASE_URL);
const MOCK_ENABLED =
  process.env.REACT_APP_ENABLE_LOCAL_AUTH_MOCK === "true" ||
  (typeof window !== "undefined" &&
    !HAS_GOOGLE_CLIENT_ID &&
    /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname));

const AuthContext = createContext(null);

function normalizeUser(rawUser) {
  if (!rawUser) return null;
  const id = rawUser.googleSub || rawUser.sub || rawUser.id || rawUser.userId;
  if (!id) return null;

  return {
    id: String(id),
    email: rawUser.email || "",
    name: rawUser.name || rawUser.displayName || rawUser.email || "Google User",
    avatarUrl: rawUser.avatarUrl || rawUser.picture || "",
  };
}

function readMockUser() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(MOCK_USER_KEY);
    if (!raw) return null;
    return normalizeUser(JSON.parse(raw));
  } catch (error) {
    console.warn("Failed to read mock auth state.", error);
    return null;
  }
}

function writeMockUser(user) {
  if (typeof window === "undefined") return;
  if (!user) {
    window.localStorage.removeItem(MOCK_USER_KEY);
    return;
  }
  window.localStorage.setItem(MOCK_USER_KEY, JSON.stringify(user));
}

async function requestJson(url, options = {}) {
  const response = await fetch(url, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });

  const payload = await response
    .json()
    .catch(() => ({ error: `Request failed with status ${response.status}` }));

  if (!response.ok) {
    throw new Error(payload?.error || `Request failed with status ${response.status}`);
  }

  return payload;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => (MOCK_ENABLED ? readMockUser() : null));
  const [isReady, setIsReady] = useState(MOCK_ENABLED);
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [authError, setAuthError] = useState("");

  const authMode = HAS_GOOGLE_CLIENT_ID ? "google" : MOCK_ENABLED ? "mock" : "disabled";
  const canUseGoogleAuth = authMode === "google" && HAS_API_BASE_URL;
  const authConfigState =
    authMode === "mock"
      ? "mock"
      : authMode === "disabled"
        ? "missing_google_client_id"
        : canUseGoogleAuth
          ? "ready"
          : "missing_api_base_url";

  const loadSession = useCallback(async () => {
    if (authMode !== "google" || !canUseGoogleAuth) {
      setIsReady(true);
      return;
    }

    try {
      const payload = await requestJson(`${API_BASE_URL}/api/auth/session`, {
        method: "GET",
      });
      setUser(normalizeUser(payload.user));
      setAuthError("");
    } catch (error) {
      setUser(null);
      setAuthError("");
    } finally {
      setIsReady(true);
    }
  }, [authMode, canUseGoogleAuth]);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const signInWithGoogleCredential = useCallback(
    async (credential) => {
      if (authMode !== "google" || !canUseGoogleAuth) {
        throw new Error("現在この環境では Google ログインを利用できません。");
      }

      setIsAuthenticating(true);
      setAuthError("");
      try {
        const payload = await requestJson(`${API_BASE_URL}/api/auth/google`, {
          method: "POST",
          body: JSON.stringify({ credential }),
        });
        setUser(normalizeUser(payload.user));
      } catch (error) {
        setAuthError(error.message);
        throw error;
      } finally {
        setIsAuthenticating(false);
      }
    },
    [authMode, canUseGoogleAuth]
  );

  const signInWithMock = useCallback(async () => {
    const mockUser = normalizeUser({
      id: "local-demo-user",
      email: "local-demo@gundamwar.test",
      name: "ローカル確認用ユーザー",
      avatarUrl: "",
    });
    writeMockUser(mockUser);
    setUser(mockUser);
    setAuthError("");
    setIsReady(true);
  }, []);

  const signOut = useCallback(async () => {
    if (authMode === "mock") {
      writeMockUser(null);
      setUser(null);
      return;
    }

    if (authMode !== "google") return;
    if (!canUseGoogleAuth) {
      setAuthError("現在この環境では Google ログインを利用できません。");
      return;
    }

    try {
      await requestJson(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        body: JSON.stringify({}),
      });
    } catch (error) {
      setAuthError(error.message);
    } finally {
      setUser(null);
    }
  }, [authMode, canUseGoogleAuth]);

  const value = useMemo(
    () => ({
      user,
      isReady,
      isAuthenticating,
      isAuthenticated: Boolean(user),
      authError,
      authMode,
      authConfigState,
      canUseGoogleAuth,
      googleClientId: GOOGLE_CLIENT_ID,
      apiBaseUrl: API_BASE_URL,
      signInWithGoogleCredential,
      signInWithMock,
      signOut,
      refreshSession: loadSession,
    }),
    [
      user,
      isReady,
      isAuthenticating,
      authError,
      authMode,
      authConfigState,
      canUseGoogleAuth,
      signInWithGoogleCredential,
      signInWithMock,
      signOut,
      loadSession,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) {
    throw new Error("useAuth must be used inside AuthProvider.");
  }
  return value;
}
